import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Printer, RotateCcw } from "lucide-react";
import { QuotationAssignmentDialog } from "@/components/quotation/QuotationAssignmentDialog";
import type {
  DailyQuotation,
  QuotationStatus,
  QuotationTarget,
} from "@/features/quotation/types";
import {
  getCuritibaDate,
  isQuotationDatePast,
} from "@/features/quotation/publication";

const STATUS_LABELS: Record<QuotationStatus, string> = {
  not_started: "Não iniciada",
  in_progress: "Aberta",
  completed: "Concluída",
};

export function formatSpecialTargets(targets?: QuotationTarget[]) {
  const names = (targets ?? [])
    .map((target) => target.name?.trim())
    .filter((name): name is string => Boolean(name));

  if (names.length <= 3) return names.join(" · ");
  return `${names.slice(0, 3).join(" · ")} + ${names.length - 3}`;
}

interface QuotationDetailHeaderProps {
  quotation?: DailyQuotation;
  topSlot?: ReactNode;
  canExportQuotation?: boolean;
  onExport?: () => void;
  selectedCount?: number;
  offerCount?: number;
  canConclude?: boolean;
  onReopen?: () => void;
  reopenPending?: boolean;
}

export function QuotationDetailHeader({
  quotation,
  topSlot,
  canExportQuotation = false,
  onExport,
  selectedCount,
  offerCount,
  canConclude = false,
  onReopen,
  reopenPending = false,
}: QuotationDetailHeaderProps) {
  const navigate = useNavigate();
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const specialTargets = formatSpecialTargets(quotation?.targets);
  const calendarDate = quotation?.date.slice(0, 10) ?? "";
  const day = new Date(`${calendarDate}T12:00:00Z`);
  const displayDate = !Number.isNaN(day.getTime())
    ? new Intl.DateTimeFormat("pt-BR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        timeZone: "UTC",
      }).format(day)
    : "—";

  return (
    <>
      <button
        type="button"
        className="lnk-back-button"
        onClick={() => navigate({ to: "/cotacoes" })}
      >
        <ArrowLeft size={17} /> Cotações
      </button>
      <header className="lnk-page-header lnk-super-header lnk-detail-header">
        <div>
          <span className="lnk-kicker">
            {quotation?.is_special ? "COTAÇÃO ESPECIAL" : "COTAÇÃO PADRÃO"}
          </span>
          <h1>{displayDate.charAt(0).toUpperCase() + displayDate.slice(1)}</h1>
          {quotation?.is_special && specialTargets ? (
            <p>{specialTargets}</p>
          ) : null}
        </div>
        <div className="lnk-super-actions">
          {canExportQuotation ? (
            <button
              type="button"
              className="quotation-export-btn"
              onClick={onExport}
            >
              <Printer size={16} /> Exportar
            </button>
          ) : null}
          {canConclude &&
          !quotation?.is_special &&
          quotation?.status === "in_progress" &&
          !isQuotationDatePast(quotation.date, getCuritibaDate()) ? (
            <button
              type="button"
              className="primary"
              onClick={() => setAssignmentOpen(true)}
            >
              <CheckCircle2 size={16} /> Concluir
            </button>
          ) : null}
          {quotation?.status === "completed" && onReopen ? (
            <button
              type="button"
              className="lnk-reopen-item"
              disabled={reopenPending}
              onClick={onReopen}
            >
              <RotateCcw size={16} /> Reabrir
            </button>
          ) : null}
        </div>
      </header>
      <div className="lnk-detail-controls">
        <div className="lnk-super-summary">
          <span>
            <strong>{selectedCount ?? "—"}</strong> produtos
          </span>
          <span>
            <strong>{offerCount ?? "—"}</strong> ofertas
          </span>
          <span>
            <strong key={quotation?.status}>
              {quotation ? STATUS_LABELS[quotation.status] : "—"}
            </strong>{" "}
            status
          </span>
        </div>
        {topSlot ?? <span />}
        <span aria-hidden="true" />
      </div>
      <QuotationAssignmentDialog
        quotation={assignmentOpen ? (quotation ?? null) : null}
        purpose="complete"
        onClose={() => setAssignmentOpen(false)}
      />
    </>
  );
}
